import { normalizeLexiconTrigramText } from '../lexicon/lexicon-trigram'
import { searchKey } from '../search/search-key'
import { assertLocalStructuralReferences } from '@w/content-blocks/src/traverse'
import type { Block, Blocks, BlockCloze, QuestionBlock } from '@w/content-blocks'
import { crdt, type Doc, type Json } from '@w/crdt'
import { pageDocument } from '@w/content-blocks-editor/doc-bindings-for-page'
import { ulid } from '@w/utils'
import { assertNoRuntimeState } from '@w/content-blocks-editor/runtime-state'
import { pageContentSchema, type PageContent } from '../server/db/schema'
import { type CardKind } from '../server/db/flashcards.schema'
import { getLexiconCardType } from '../lexicon/lexicon-cards'
import { readPromptType } from './question-card'

/** Lexicon adoption persists its mint-time blocks alongside the unchanged entry payload. */
export type PageDocumentContent = PageContent | (
  Extract<PageContent, { kind: 'lexiconEntry' }> & { children: Blocks }
)

export type PageDocumentJson = {
  title: string
  translatable?: true
  readonly?: true
  content: PageDocumentContent
  archived?: boolean
  deletedAt: number | null
}

export type PageQuestionBlock = QuestionBlock | BlockCloze
export type ProjectedQuestion = {
  qid: string
  blockId: string
  cardKind: CardKind | null
  fractionalIndex: string
  importance: 'low' | 'medium' | 'high'
  difficulty: 'low' | 'medium' | 'high'
  promptType: ReturnType<typeof readPromptType>
  block: PageQuestionBlock
}
export type PageProjections = {
  childPages: { id: string; title: string }[]
  media: string[]
  questions: ProjectedQuestion[]
}

export const UNTITLED_PAGE = 'Untitled page'

export type PageScalarFields = Partial<Pick<PageDocumentJson, 'title' | 'deletedAt' | 'archived'>> & { translatable?: boolean; readonly?: boolean; content?: PageDocumentContent }

/** Product metadata stays in replicated columns; only nested authored structure enters the CRDT. */
export function pageStructure(fields: Pick<PageDocumentJson, 'content'>) {
  const content = fields.content as unknown as Record<string, unknown>
  const { kind: _kind, language: _language, entry, ...structure } = content
  if (entry && typeof entry === 'object') {
    structure.entry = Object.fromEntries(Object.entries(entry).filter(([key, value]) => !isEntryScalarField(key, value)))
  }
  return { content: pageDocumentInput(structure) }
}

/** Reject metadata authored through a CRDT operation before it reaches storage. */
export function assertPageStructure(json: unknown): void {
  if (!isRecord(json) || !isRecord(json.content) || Object.keys(json).some(key => key !== 'content') ||
    'kind' in json.content || 'language' in json.content) throw new Error('Invalid page document: scalar fields require plain columns')
  if (isRecord(json.content.entry) && Object.entries(json.content.entry).some(([key, value]) => isEntryScalarField(key, value))) {
    throw new Error('Invalid page document: entry scalars require plain columns')
  }
}

/** Entry labels remain scalar-column data even though their value is a map. */
export function isEntryScalarField(key: string, value: unknown): boolean {
  return key === 'translations' || value === null || typeof value !== 'object'
}

export function pageScalarValues(fields: PageScalarFields) {
  const content = fields.content
  const entry = content?.kind === 'lexiconEntry' ? content.entry : undefined
  const title = fields.title ?? UNTITLED_PAGE
  return {
    ...(fields.title === undefined ? {} : { title, titleKey: searchKey(title) }),
    ...(fields.deletedAt === undefined ? {} : { deletedAt: fields.deletedAt }),
    ...(fields.archived === undefined ? {} : { archived: fields.archived }),
    ...(fields.translatable === undefined ? {} : { translatable: fields.translatable }),
    ...(fields.readonly === undefined ? {} : { readonly: fields.readonly }),
    ...(content === undefined ? {} : { kind: content.kind,
      language: content.kind === 'language' ? content.language : entry?.language ?? null }),
    ...(entry === undefined ? {} : {
      entryId: entry.id, entryKind: entry.kind, translations: entry.translations, pos: 'pos' in entry ? entry.pos : null,
      inflectionClass: 'inflectionClass' in entry ? entry.inflectionClass : null,
      headword: entry.headword, headwordKey: normalizeLexiconTrigramText(entry.headword).trim(), transcription: entry.transcription ?? null,
      audioSrc: entry.audioSrc ?? null,
      readAloudQuestionId: 'readAloudQuestionId' in entry ? entry.readAloudQuestionId : null,
      dictationQuestionId: 'dictationQuestionId' in entry ? entry.dictationQuestionId : null,
    }),
  }
}

/** `user` authors the page's changes: crdt_rs stamps each change and the engine admits it only from the committing
 * connection's session user (db-crdt-sync :68; crdt facade.spec Author "Precedence": edit user, Session.user, actAs). */
export function newPageDocument(fields: PageDocumentJson, user?: string) {
  const doc = crdt.create({}, { user })
  doc.edit(tx => { tx.set('', 'content', pageStructure(fields).content as Json) })
  return doc
}

export function newPageBaseline(fields: PageDocumentJson, user?: string) {
  return crdt.create(pageStructure(fields) as Json, { user })
}

/** Import editor values once; persisted documents use engine text and map identities. */
export function pageDocumentInput(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(item => pageDocumentInput(item))
  if (!isRecord(value)) return value
  const result = Object.fromEntries(Object.entries(value)
    .filter(([, item]) => item !== undefined)
    .map(([key, item]) => [key, pageDocumentInput(item)]))
  return value.type === 'rule' ? ruleIdentities(result) : result
}

/**
 * A member/affix rule owns one class card; markdown authoring has no place to write its identity, so the document
 * producer mints it once, exactly as the editor's question templates do. A rule with authored clozes is no card itself
 * (flashcards-training.requirements.md 2026-11-02): each recall cloze is its own question and the `.practice` clozes
 * share one qid (the parser mints it). A page stored when every cloze carried the rule's qid gets fresh recall qids
 * once; its practice clozes keep that qid, so the rule keeps it only while a practice cloze shares it.
 */
function ruleIdentities(rule: Record<string, unknown>): Record<string, unknown> {
  const { qid: stored, ...rest } = rule
  const qid = typeof stored === 'string' && stored ? stored : undefined
  if (!hasCloze(rule.children)) return { ...rest, qid: qid ?? ulid() }
  if (!qid) return rest
  const children = withOwnRecallQids(rule.children, qid)
  return hasCloze(children, qid) ? { ...rest, children, qid } : { ...rest, children }
}

function withOwnRecallQids(value: unknown, ruleQid: string): unknown {
  if (Array.isArray(value)) return value.map(item => withOwnRecallQids(item, ruleQid))
  if (!isRecord(value)) return value
  const next = value.type === 'cloze' && value.practice !== true && value.qid === ruleQid ? { ...value, qid: ulid() } : value
  return 'children' in next ? { ...next, children: withOwnRecallQids(next.children, ruleQid) } : next
}

/** Compose a public page value from structure plus authoritative plain columns. */
export function readPageDocument(json: unknown, row?: Record<string, unknown>): PageDocumentJson {
  assertNoRuntimeState(json)
  assertLocalStructuralReferences(json)
  if (!isRecord(json) || !isRecord(json.content)) throw new Error('Invalid page document')
  const scalars=row??json
  const kind=row?row.kind:json.content.kind
  if(typeof scalars.title!=='string'||typeof kind!=='string'||
    !(scalars.deletedAt===null||typeof scalars.deletedAt==='number'&&Number.isFinite(scalars.deletedAt))||
    (row?typeof row.translatable!=='boolean':('translatable' in json&&json.translatable!==true))||
    (row?row.readonly!==undefined&&typeof row.readonly!=='boolean':('readonly' in json&&json.readonly!==true))||
    (row?typeof row.archived!=='boolean':('archived' in json&&typeof json.archived!=='boolean')))throw new Error('Invalid page document')
  const content = { ...json.content, kind }
  if (content.kind === 'language') Object.assign(content, { language: row?row.language:json.content.language })
  if (content.kind === 'lexiconEntry' && row) {
    const entry = isRecord(json.content.entry) ? json.content.entry : {}
    Object.assign(content, { entry: { ...entry, translations: row.translations ?? {}, id: row.entryId, kind: row.entryKind, language: row.language,
      headword: row.headword, ...((row.pos === null || row.pos === undefined) ? {} : { pos: row.pos }),
      ...((row.inflectionClass === null || row.inflectionClass === undefined) ? {} : { inflectionClass: row.inflectionClass }),
      ...((row.transcription === null || row.transcription === undefined) ? {} : { transcription: row.transcription }),
      ...((row.audioSrc === null || row.audioSrc === undefined) ? {} : { audioSrc: row.audioSrc }),
      ...((row.readAloudQuestionId === null || row.readAloudQuestionId === undefined) ? {} : { readAloudQuestionId: row.readAloudQuestionId }),
      ...((row.dictationQuestionId === null || row.dictationQuestionId === undefined) ? {} : { dictationQuestionId: row.dictationQuestionId }) } })
  }
  const value = { title:scalars.title,deletedAt:scalars.deletedAt,
    ...(scalars.translatable === true ? { translatable: true as const } : {}),
    ...(scalars.readonly === true ? { readonly: true as const } : {}),
    ...(scalars.archived === true ? { archived: true } : {}), content }
  if (!pageContentSchema.safeParse(content).success) throw new Error('Invalid page document')
  if ('children' in content) validateBlocks(content.children)
  return value as PageDocumentJson
}

// INV-2: projections depend only on the merged page JSON; no database, clock, or session state.
export function projectPage(pageId: string, doc: PageDocumentJson): PageProjections {
  readPageDocument(doc)
  const result: PageProjections = { childPages: [], media: [], questions: [] }
  if (doc.deletedAt !== null) return result
  if ('children' in doc.content) {
    const questionIds = new Map<string, ProjectedQuestion>()
    const blocks = pageDocument({ toJSON: () => doc as unknown as ReturnType<Doc['toJSON']> }).content.children
    // cardKind is a card column the lexicon commit writes; the projection reads the same entry identity map.
    const entry = doc.content.kind === 'lexiconEntry' ? doc.content.entry : undefined
    visitBlocks(blocks, (block) => {
      if ((block.type === 'img' || block.type === 'audio' || block.type === 'video' || block.type === 'file') && block.src && !result.media.includes(block.src)) {
        result.media.push(block.src)
      }
      if (block.type === 'page' && block.pageId !== pageId) {
        result.childPages.push({ id: block.pageId, title: UNTITLED_PAGE })
      }
      // A member/affix rule owns one class card, projected per review. A rule with authored clozes is no card itself:
      // its recall clozes and its shared practice qid project below as any cloze does.
      if (block.type === 'rule' && !hasCloze(block.children)) {
        const qid = (block as Block & { qid?: string }).qid
        if (!qid) throw new Error('Rule missing question id')
        const question: PageQuestionBlock = { type: 'cloze', id: block.id,
          qid, fractionalIndex: 'a0', children: [] }
        const projected: ProjectedQuestion = { qid: qid, blockId: block.id,
          cardKind: null, fractionalIndex: 'a0', importance: 'medium', difficulty: 'medium',
          promptType: 'cloze', block: question }
        questionIds.set(qid, projected)
        result.questions.push(projected)
      }
      if (block.type !== 'question' && block.type !== 'cloze') return
      const question: PageQuestionBlock = block
      const projected: ProjectedQuestion = {
        qid: question.qid, blockId: question.id, cardKind: entry ? getLexiconCardType(entry, question.qid) ?? null : null,
        fractionalIndex: question.fractionalIndex,
        importance: question.importance === 'low' || question.importance === 'high' ? question.importance : 'medium',
        difficulty: question.difficulty === 'low' || question.difficulty === 'high' ? question.difficulty : 'medium',
        promptType: readPromptType(question), block: question,
      }
      const previous = questionIds.get(question.qid)
      // First part owns the complete defaults tuple. Later parts keep their grading shape.
      if (previous) return
      questionIds.set(question.qid, projected)
      result.questions.push(projected)
    })
  }
  return result
}

/** Whether `value` holds a cloze (carrying `qid`, when given) at any depth. */
function hasCloze(value: unknown, qid?: string): boolean {
  if (Array.isArray(value)) return value.some(item => hasCloze(item, qid))
  if (!isRecord(value)) return false
  if (value.type === 'cloze' && (qid === undefined || value.qid === qid)) return true
  return hasCloze(value.children, qid)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function validateBlocks(value: unknown): void {
  if (!Array.isArray(value)) throw new Error('Invalid page blocks')
  for (const block of value) {
    if (!isRecord(block) || typeof block.id !== 'string' || typeof block.type !== 'string') {
      throw new Error('Invalid page block')
    }
    if ((block.type === 'question' || block.type === 'cloze') &&
      (typeof block.qid !== 'string' || !block.qid ||
        typeof block.fractionalIndex !== 'string' || !block.fractionalIndex)) {
      throw new Error('Invalid question block')
    }
    if ((block.type === 'pageLink' || block.type === 'page') && typeof block.pageId !== 'string') throw new Error('Invalid page link')
    for (const key of ['children', 'question', 'explanation', 'author', 'rows', 'cols', 'caption']) {
      if (block[key] !== undefined) validateBlocks(block[key])
    }
    if (block.opts !== undefined) {
      if (!Array.isArray(block.opts)) throw new Error('Invalid question options')
      for (const option of Object.values(block.opts)) validateBlocks(option)
    }
  }
}

/** Visit all nested block fields, including question options and table cells. */
function visitBlocks(value: unknown, visit: (block: Block, parent?: Block) => void, parent?: Block): void {
  if (Array.isArray(value)) {
    for (const item of value) visitBlocks(item, visit, parent)
  } else if (isRecord(value)) {
    const block = typeof value.type === 'string' && typeof value.id === 'string' ? value as Block : undefined
    if (block) visit(block, parent)
    for (const child of Object.values(value)) visitBlocks(child, visit, block ?? parent)
  }
}
